import type { Path } from "react-hook-form";

/**
 * FastAPI validation error structure
 * Used for mapping backend validation errors to frontend form fields
 */
export interface ApiValidationError {
	type: string;
	loc: (string | number)[];
	msg: string;
	ctx?: { error?: string };
}

/**
 * Utility to map API error location to react-hook-form field path
 *
 * @param loc - The location array from FastAPI validation error
 * @param detailFields - Optional array of field names that belong to a detail/nested object
 * @returns Form field path string, or null for root-level errors (shown as toast)
 *
 * @example
 * // Simple field mapping
 * mapApiErrorToFieldPath(['body', 'nombre']) // => 'nombre'
 *
 * @example
 * // Nested field mapping (for property forms with detail object)
 * mapApiErrorToFieldPath(['body', 'tipo'], PROPERTY_DETAIL_FIELDS) // => 'detail.tipo'
 *
 * @example
 * // Root error (shown as toast, not field error)
 * mapApiErrorToFieldPath(['body', '__root__']) // => null
 */
export function mapApiErrorToFieldPath(
	loc: (string | number)[],
	detailFields?: readonly string[],
): string | null {
	const relevantPath = loc.filter(
		(part) => part !== "body" && part !== "__root__",
	);

	// Root errors should be shown as toast notifications
	if (loc.includes("__root__") && relevantPath.length === 0) {
		return null;
	}

	// For forms with nested detail object (e.g., property.detail pattern)
	if (relevantPath.length === 1 && detailFields) {
		const field = relevantPath[0] as string;
		if (detailFields.includes(field)) {
			return `detail.${field}`;
		}
		return `property.${field}`;
	}

	return relevantPath.join(".");
}

/**
 * Hook for handling API validation errors with react-hook-form
 *
 * @param setError - The setError function from react-hook-form
 * @param apiErrors - Array of API validation errors
 * @param detailFields - Optional array of nested field names
 *
 * @example
 * const { setError } = useForm<FormData>();
 *
 * useEffect(() => {
 *   handleApiErrors(setError, apiErrors);
 * }, [apiErrors, setError]);
 */
export function handleApiErrors<TFieldValues>(
	setError: (
		name: Path<TFieldValues>,
		error: { type: string; message: string },
	) => void,
	apiErrors: ApiValidationError[] | undefined,
	detailFields?: readonly string[],
): void {
	if (!apiErrors || apiErrors.length === 0) return;

	apiErrors.forEach((err) => {
		const fieldPath = mapApiErrorToFieldPath(err.loc, detailFields);
		if (fieldPath) {
			setError(fieldPath as Path<TFieldValues>, {
				type: "api",
				message: err.ctx?.error || err.msg,
			});
		}
	});
}
